import { Component, OnInit } from '@angular/core';
import { DataServiceService } from '../services/data-service.service';
import { Customers, TrailerLocation, CustomerAddress, CustomerContact } from '../interfaces';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AuthService } from '../services/auth.service';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-customers',
  templateUrl: './customers.component.html',
  styleUrl: './customers.component.scss'
})

export class CustomersComponent implements OnInit {
selectedCustomerType: any;

  constructor(private dataServiceService: DataServiceService,
    private messageService: MessageService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmService: ConfirmationService) {
  }

  customers: Customers[] = [];
  filteredCustomers: Customers[] = [];
  searchTerm: string = '';
  selectedStatus: string | null = null;
  locations: TrailerLocation[] = []
  displayAddDialog = false;
  newCustomer: any = {}
  newLocation: any = {}
  displayEditDialog = false;
  displayAddressSelectionDialog = false;
  displayAddAddressDialog = false;
  selectedCustomer: any = {};
  savingEditedCustomer: boolean = false;
  selectedAddress: any = null;
  currentCustomer: Customers | null = null;
  customerToEdit: Customers | null = null;
  isAdmin = false;
  hasAccess = false;
  
  // Track active tab index for each customer with multiple addresses
  customerActiveTabIndex: { [customerId: number]: number } = {};

  statusOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' }
  ];

  customerTypeOptions = [
    { label: 'Both', value: 'Both' },
    { label: 'Receiver', value: 'Receiver' },
    { label: 'Supplier', value: 'Supplier' }
  ];

  availableCustomers: Customers[] = [];

  // Methods for relationship management
  get shouldShowRelationships(): boolean {
    return this.newCustomer.customer_type === 'Supplier' || this.newCustomer.customer_type === 'Receiver' || this.newCustomer.customer_type === 'Both';
  }

  get availableSuppliersForRelationship(): Customers[] {
    return this.availableCustomers.filter(customer => 
      customer.customer_id !== this.newCustomer.customer_id
    );
  }

  get availableReceiversForRelationship(): Customers[] {
    return this.availableCustomers.filter(customer => 
      customer.customer_id !== this.newCustomer.customer_id
    );
  }

  onCustomerTypeChange() {
    // Clear relationship selections when customer type changes
    if (!this.shouldShowRelationships) {
      this.newCustomer.delivers_to = [];
      this.newCustomer.receives_from = [];
    }
  }

  // Helper method to get customer name for relationship display
  getCustomerNameById(customerId: number): string {
    const customer = this.availableCustomers.find(c => c.customer_id === customerId);
    return customer ? customer.name : '';
  }

  // Event handlers for child components
  onContactUpdated() {
    this.refreshCustomersData();
  }

  onContactDeleted() {
    this.refreshCustomersData();
  }

  onAddressSaved() {
    this.refreshCustomersData();
  }

  // Method to refresh customers data
  private refreshCustomersData() {
    this.dataServiceService.getAllCustomers().toPromise().then((customersData) => {
      this.customers = customersData;
      this.filteredCustomers = customersData;
      this.availableCustomers = customersData;
      this.locations = customersData;
      this.filterCustomers();
    });
  }

  // Method to open add address dialog
  openAddAddressDialog() {
    this.displayAddAddressDialog = true;
  }

  // Method to confirm address deletion
  confirmDeleteAddress(customer: Customers, address: any) {
    this.confirmService.confirm({
      message: `Are you sure you want to delete the address "${address.address}, ${address.city}, ${address.state}"? This action cannot be undone.`,
      header: 'Delete Address',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      accept: () => {
        this.deleteAddress(customer, address);
      }
    });
  }

  // Method to delete address
  deleteAddress(customer: Customers, address: any) {
    const deleteData = {
      name: customer.name,
      status: customer.status,
      customer_type: customer.customer_type,
      delivers_to: customer.receivers?.map(r => r.customer_id) || [],
      receives_from: customer.suppliers?.map(s => s.customer_id) || [],
      delete_address_ids: [address.address_id]
    };

    this.dataServiceService.updateCustomer(customer.customer_id, deleteData)
      .subscribe({
        next: (response) => {
          // Remove the address from the local UI data immediately
          const customerIndex = this.customers.findIndex((c: Customers) => c.customer_id === customer.customer_id);
          if (customerIndex > -1 && this.customers[customerIndex].addresses) {
            const addressIndex = this.customers[customerIndex].addresses.findIndex(
              (a: any) => a.address_id === address.address_id
            );
            if (addressIndex > -1) {
              this.customers[customerIndex].addresses.splice(addressIndex, 1);
            }
          }

          // Update customerToEdit if it's the same customer
          if (this.customerToEdit && this.customerToEdit.customer_id === customer.customer_id) {
            const addressIndexInEditCustomer = this.customerToEdit.addresses.findIndex(
              (a: any) => a.address_id === address.address_id
            );
            if (addressIndexInEditCustomer > -1) {
              this.customerToEdit.addresses.splice(addressIndexInEditCustomer, 1);
            }
          }

          // Update filtered customers as well
          this.filterCustomers();

          // Close address selection dialog if no addresses left or only one remains
          if (this.customerToEdit && this.customerToEdit.addresses.length <= 1) {
            this.displayAddressSelectionDialog = false;
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Address deleted successfully'
          });

          // Refresh customers data in background for consistency
          Promise.all([
            this.dataServiceService.getAllCustomers().toPromise(),
            this.dataServiceService.getAllCustomers().toPromise()
          ]).then(([customersData, locationsData]) => {
            this.customers = customersData;
            this.filteredCustomers = customersData;
            this.locations = locationsData;
            this.filterCustomers();
          });
        },
        error: (error) => {
          console.error('Address deletion error:', error);
          let errorMessage = 'Failed to delete address';
          if (error.error?.detail) {
            errorMessage = error.error.detail;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
        }
      });
  }

  // Helper methods to display customer relationships
  getSuppliesTo(customer: Customers): string[] {
    return customer.receivers?.map(r => r.name) || [];
  }

  getReceivesFromCustomers(customer: Customers): string[] {
    return customer.suppliers?.map(s => s.name) || [];
  }

  hasRelationships(customer: Customers): boolean {
    return (customer.suppliers && customer.suppliers.length > 0) || 
           (customer.receivers && customer.receivers.length > 0);
  }

  get menuItems() {
    // Always include the View Loads option
    const items = [
      { 
        label: 'View Loads',
        icon: 'pi pi-truck',
        command: () => this.showDialog(this.currentCustomer) 
      }
    ];
    
    // Only add Edit Customer option if user has access
    if (this.hasAccess) {
      items.unshift({
        label: 'Edit Customer',
        icon: 'pi pi-pencil',
        command: () => this.currentCustomer && this.editCustomer(this.currentCustomer)
      });
    }
    
    return items;
  }

  // Helper methods for new customer structure
  getPrimaryAddress(customer: Customers): CustomerAddress | null {
    return customer.addresses && customer.addresses.length > 0 ? customer.addresses[0] : null;
  }

  getCustomerAddress(customer: Customers): string {
    const primaryAddress = this.getPrimaryAddress(customer);
    return primaryAddress ? primaryAddress.address : (customer.address || '');
  }

  getCustomerCity(customer: Customers): string {
    const primaryAddress = this.getPrimaryAddress(customer);
    return primaryAddress ? primaryAddress.city : (customer.city || '');
  }

  getCustomerState(customer: Customers): string {
    const primaryAddress = this.getPrimaryAddress(customer);
    return primaryAddress ? primaryAddress.state : (customer.state || '');
  }

  getCustomerZipcode(customer: Customers): string {
    const primaryAddress = this.getPrimaryAddress(customer);
    return primaryAddress ? primaryAddress.zipcode : (customer.zipcode || '');
  }

  getCustomerFullAddress(customer: Customers): string {
    const address = this.getCustomerAddress(customer);
    const city = this.getCustomerCity(customer);
    const state = this.getCustomerState(customer);
    const zipcode = this.getCustomerZipcode(customer);
    
    const parts = [address, city, state, zipcode].filter(part => part && part !== 'nan');
    return parts.join(', ');
  }

  isSupplier(customer: Customers): boolean {
    return customer.role ? customer.role.is_supplier : false;
  }

  isReceiver(customer: Customers): boolean {
    return customer.role ? customer.role.is_receiver : false;
  }

  getCustomerType(customer: Customers): string {
    return customer.customer_type || 'Unknown';
  }

  hasMultipleAddresses(customer: Customers): boolean {
    return customer.addresses && customer.addresses.length > 1;
  }

  // Helper method to get active tab index for a customer
  getActiveTabIndex(customerId: number): number {
    return this.customerActiveTabIndex[customerId] || 0;
  }

  // Helper method to set active tab index for a customer
  setActiveTabIndex(customerId: number, tabIndex: number): void {
    this.customerActiveTabIndex[customerId] = tabIndex;
  }

  // Helper methods for service hours
  private convertTo12Hour(time: string | null): string {
    if (!time) return '';
    
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const min = minutes || '00';
    
    if (hour === 0) {
      return `12:${min} AM`;
    } else if (hour < 12) {
      return `${hour}:${min} AM`;
    } else if (hour === 12) {
      return `12:${min} PM`;
    } else {
      return `${hour - 12}:${min} PM`;
    }
  }

  getServiceHours(address: CustomerAddress): string {
    if (address.service_hours_start && address.service_hours_end) {
      return `${this.convertTo12Hour(address.service_hours_start)} - ${this.convertTo12Hour(address.service_hours_end)}`;
    } else if (address.service_hours_start) {
      return `Opens: ${this.convertTo12Hour(address.service_hours_start)}`;
    } else if (address.service_hours_end) {
      return `Closes: ${this.convertTo12Hour(address.service_hours_end)}`;
    }
    return '';
  }

  hasServiceHours(address: CustomerAddress): boolean {
    return !!(address.service_hours_start || address.service_hours_end);
  }

  // Helper methods for contacts
  hasContacts(address: CustomerAddress): boolean {
    return address.contacts && address.contacts.length > 0;
  }

  getContactCount(address: CustomerAddress): number {
    return address.contacts ? address.contacts.length : 0;
  }

  getPrimaryContact(address: CustomerAddress): CustomerContact | null {
    return address.contacts && address.contacts.length > 0 ? address.contacts[0] : null;
  }

  ngOnInit() {
    // Load customers data
    this.dataServiceService.getAllCustomers().subscribe(data => {
      this.filteredCustomers = data
      this.customers = data;
      this.availableCustomers = data;
      this.locations = data; // For relationship dropdowns
      // Safe access to customer data for debugging
      if (data.length > 0) {
        const primaryAddress = this.getPrimaryAddress(data[0]);
        const primaryContact = primaryAddress ? this.getPrimaryContact(primaryAddress) : null;
        if (primaryContact?.name) {
          console.log('First customer primary contact:', primaryContact.name);
        }
      }
    })
    
    // // Load trailer locations
    // this.dataServiceService.getAllCustomers().subscribe(data => {
    //   this.locations = data;
    // })
    
    // Load user profile and set access
    this.authService.fetchUserProfile().subscribe(user =>
      this.isAdmin = user?.roles?.includes('Admin') ?? false
    );
    this.dataServiceService.customers$.subscribe(customers => {
      this.customers = customers;
      this.filteredCustomers = customers;
    })
    this.authService.userProfile$.subscribe((profile) => {
      if (profile?.roles?.includes('Dispatcher') || profile?.roles?.includes('Admin')) {
        this.hasAccess = true;
      } else {
        this.hasAccess = false;
      }
    });

    // Initialize the form
    this.resetNewCustomerForm();

    // Handle route parameters for scrolling to specific customer
    this.route.queryParams.subscribe(params => {
      if (params['customerId'] && params['scrollTo']) {
        setTimeout(() => {
          this.scrollToCustomer(+params['customerId'], params['expand'] === 'true', params['addressId'] ? +params['addressId'] : undefined);
        }, 500); // Give time for data to load and render
      }
    });
  }


  filterCustomers() {
    this.filteredCustomers = this.customers.filter(customer => {
      const location = this.locations.find(loc => loc.customer_id === customer.customer_id);
      const locationName = location ? location.name : 'Unknown';

      const matchesSearch = !this.searchTerm ||
        locationName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        customer.name.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesStatus = !this.selectedStatus ||
        customer.status === this.selectedStatus;

      const matchesCustomerType = !this.selectedCustomerType ||
        customer.customer_type === this.selectedCustomerType;

      return matchesSearch && matchesStatus && matchesCustomerType;
    });
  }

  getCustomerName(customerId: number) {
    const customer = this.locations.find(c => c.customer_id === customerId);
    return customer ? customer.name : 'Unknown';
  }

  addCustomer() {
    this.displayAddDialog = true;
  }

  onCustomerSaved() {
    // Refresh data when customer is saved
    Promise.all([
      this.dataServiceService.getAllCustomers().toPromise(),
      this.dataServiceService.getAllCustomers().toPromise()
    ]).then(([customersData, locationsData]) => {
      this.customers = customersData;
      this.filteredCustomers = customersData;
      this.locations = locationsData;
      this.availableCustomers = customersData; // Update available customers for relationships
      this.filterCustomers();
    });
  }

  private resetNewCustomerForm() {
    this.newCustomer = {
      name: '',
      status: 'Active',
      customer_type: '',
      delivers_to: [],
      receives_from: []
    };
  }

  editCustomer(customer: Customers) {
    this.customerToEdit = customer;
    
    // Check if customer has multiple addresses
    if (this.hasMultipleAddresses(customer)) {
      this.displayAddressSelectionDialog = true;
    } else {
      // Single address - proceed directly to edit
      const primaryAddress = this.getPrimaryAddress(customer);
      this.proceedToEditCustomer(customer, primaryAddress);
    }
  }

  selectAddressToEdit(address: any) {
    this.selectedAddress = address;
    this.displayAddressSelectionDialog = false;
    this.proceedToEditCustomer(this.customerToEdit!, address);
  }

  addNewAddressToCustomer() {
    // Close the address selection dialog
    this.displayAddressSelectionDialog = false;
    
    // Set up selected customer for adding new address
    this.selectedCustomer = {
      customer_id: this.customerToEdit!.customer_id,
      name: this.customerToEdit!.name,
      status: this.customerToEdit!.status,
      customer_type: this.customerToEdit!.customer_type,
      contact_person: this.customerToEdit!.contact_name || '',
      email: this.customerToEdit!.email || '',
      phone: this.customerToEdit!.phone || '',
      delivers_to: this.customerToEdit!.receivers?.map(r => r.customer_id) || [],
      receives_from: this.customerToEdit!.suppliers?.map(s => s.customer_id) || []
    };
    
    // Open the add new address dialog
    this.openAddAddressDialog();
  }

  proceedToEditCustomer(customer: Customers, address: any) {
    // Get the location that matches the customer_id (for backward compatibility)
    const location = this.locations.find(loc => loc.customer_id === customer.customer_id);

    // Use provided address or fallback to primary
    const selectedAddress = address || this.getPrimaryAddress(customer);
    const primaryContact = selectedAddress ? this.getPrimaryContact(selectedAddress) : null;

    // Create a deep copy of the customer object
    this.selectedCustomer = {
      customer_id: customer.customer_id,
      name: customer.name || location?.name || '',
      contact_person: primaryContact?.name || customer.contact_name || '',
      email: primaryContact?.email || customer.email || '',
      phone: primaryContact?.phone || customer.phone || '',
      status: customer.status || 'Active',
      address: selectedAddress?.address || this.getCustomerAddress(customer),
      city: selectedAddress?.city || this.getCustomerCity(customer),
      state: selectedAddress?.state || this.getCustomerState(customer),
      zipcode: selectedAddress?.zipcode || this.getCustomerZipcode(customer),
      notes: selectedAddress?.notes || null,
      service_hours_start: selectedAddress?.service_hours_start || null,
      service_hours_end: selectedAddress?.service_hours_end || null,
      customer_type: this.getCustomerType(customer),
      addresses: customer.addresses || [],
      selectedAddressId: selectedAddress?.address_id || null,
      selectedAddressContacts: selectedAddress?.contacts || [],
      role: customer.role || { is_supplier: false, is_receiver: false },
      suppliers: customer.suppliers || [],
      receivers: customer.receivers || [],
      delivers_to: customer.receivers?.map(r => r.customer_id) || [],
      receives_from: customer.suppliers?.map(s => s.customer_id) || []
    };
    this.displayEditDialog = true;
  }

  saveEditedCustomer() {
    this.savingEditedCustomer = true;
    // Get primary contact data from the first contact in the list
    const primaryContact = this.selectedCustomer.selectedAddressContacts && 
                          this.selectedCustomer.selectedAddressContacts.length > 0 
                          ? this.selectedCustomer.selectedAddressContacts[0] 
                          : null;

    const customerToUpdate = {
      name: this.selectedCustomer.name?.trim(),
      status: this.selectedCustomer.status,
      customer_type: this.selectedCustomer.customer_type,
      contact_person: primaryContact?.name?.trim() || this.selectedCustomer.contact_person?.trim() || null,
      email: primaryContact?.email?.trim() || this.selectedCustomer.email?.trim() || null,
      phone: primaryContact?.phone?.trim() || this.selectedCustomer.phone?.trim() || null,
      address: this.selectedCustomer.address?.trim() || null,
      city: this.selectedCustomer.city?.trim() || null,
      state: this.selectedCustomer.state?.trim() || null,
      zipcode: this.selectedCustomer.zipcode?.trim() || null,
      notes: this.selectedCustomer.notes?.trim() || '',
      service_hours_start: this.selectedCustomer.service_hours_start || null,
      service_hours_end: this.selectedCustomer.service_hours_end || null,
      delivers_to: this.selectedCustomer.delivers_to || [],
      receives_from: this.selectedCustomer.receives_from || [],
      // Add all contacts for the address
      contacts: this.selectedCustomer.selectedAddressContacts || [],
      // Specify which address is being updated
      address_id: this.selectedCustomer.selectedAddressId
    };

    this.dataServiceService.updateCustomer(this.selectedCustomer.customer_id, customerToUpdate)
      .subscribe({
        next: (response) => {
          this.savingEditedCustomer = false;
          this.displayEditDialog = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Customer updated successfully'
          });
          // Use the centralized refresh method
          this.refreshCustomersData();
        },
        error: (error) => {
          this.savingEditedCustomer = false;
          console.error('Customer update error:', error);
          let errorMessage = 'Failed to update customer';
          if (error.error?.detail) {
            errorMessage = error.error.detail;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
        }
      });
  }

  setSelectedCustomer(customer: Customers) {
    this.currentCustomer = customer;
  }

  deleteCustomer(customerId: number) {
    this.confirmService.confirm({
      message: 'Are you sure you want to delete this customer?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
      rejectButtonStyleClass: 'p-button-danger p-button-outlined p-button-sm',
      accept: () => {
        this.dataServiceService.deleteCustomer(customerId).subscribe({
          next: () => {
            this.displayEditDialog = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Customer deleted successfully'
            });

            // Refresh both customers and locations data
            Promise.all([
              this.dataServiceService.getAllCustomers().toPromise(),
              this.dataServiceService.getAllCustomers().toPromise()
            ]).then(([customersData, locationsData]) => {
              this.customers = customersData;
              this.filteredCustomers = customersData;
              this.locations = locationsData;
            });
          },
          error: (error) => {
            console.error('Customer deletion error:', error);
            let errorMessage = 'Failed to delete customer';

            if (error.error?.detail) {
              errorMessage = error.error.detail;
            }

            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorMessage
            });
          }
        });
      }
    });
  }

  showDialog(customer: any) {
    this.router.navigate(['/customer-load-details', customer.customer_id]);
  }

  scrollToCustomer(customerId: number, shouldExpand: boolean = false, addressId?: number) {
    const customer = this.filteredCustomers.find(c => c.customer_id === customerId);
    if (!customer) {
      console.warn('Customer not found in filtered list:', customerId);
      return;
    }
  
    if (addressId && this.hasMultipleAddresses(customer)) {
      const addressIndex = customer.addresses.findIndex(addr => addr.address_id === addressId);
      if (addressIndex !== -1) {
        this.customerActiveTabIndex[customerId] = addressIndex;
      }
    }
  
    // Use longer timeout and better container detection
    setTimeout(() => {
      const customerElement = document.getElementById(`customer-${customerId}`);
      if (!customerElement) {
        console.warn('Customer element not found:', `customer-${customerId}`);
        return;
      }
  
      // Find scrollable container more reliably
      let scrollContainer = this.findScrollableParent(customerElement);
      
      if (!scrollContainer) {
        console.warn('No scrollable container found');
        return;
      }
  
      // Better scroll calculation using offsetTop
      const elementTop = customerElement.offsetTop;
      const containerHeight = scrollContainer.clientHeight;
      const elementHeight = customerElement.offsetHeight;
      const centerPosition = elementTop - (containerHeight / 2) + (elementHeight / 2);
  
      console.log('Scroll calculation:', {
        elementTop,
        containerHeight,
        elementHeight,
        centerPosition,
        currentScrollTop: scrollContainer.scrollTop
      });
  
      scrollContainer.scrollTo({
        top: Math.max(0, centerPosition),
        behavior: 'smooth'
      });
  
      // Highlight logic (unchanged)
      this.highlightCustomerCard(customerElement);
  
    }, 300); // Increased timeout
  }
  
  private findScrollableParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    
    while (parent) {
      const style = window.getComputedStyle(parent);
      const hasScroll = parent.scrollHeight > parent.clientHeight;
      const isScrollable = style.overflowY === 'auto' || style.overflowY === 'scroll';
      
      if (hasScroll && isScrollable) {
        console.log('Found scrollable parent:', parent);
        return parent;
      }
      parent = parent.parentElement;
    }
    
    return null;
  }
  
  private highlightCustomerCard(customerElement: HTMLElement) {
    const cardElement = customerElement.querySelector('p-card .p-card') as HTMLElement;
    if (cardElement) {
      cardElement.style.border = '2px solid rgb(208, 220, 216)';
      cardElement.style.boxShadow = '0 0 15px rgba(253, 255, 255, 0.94)';
      cardElement.style.transition = 'all 0.3s ease';
      
      setTimeout(() => {
        cardElement.style.border = '';
        cardElement.style.boxShadow = '';
      }, 1500);
    }
  }
}
